import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, ButtonBase, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogContent, DialogTitle, Divider, IconButton, Skeleton, Slider, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import { tariffsApi } from '../services/api';
import { useShell } from '../components/Layout';
import MarketplaceIcon from '../components/common/MarketplaceIcon';
import TrialCard from '../components/TrialCard';
import TestTariffCard from '../components/TestTariffCard';
import { ErrorState } from '../components/common/States';
import { apiError, formatDate, formatMoney, pluralize } from '../utils/format';
import {
  accountsLabel, formatSignedMoney, isDiscountLine, lineDetail, lineLabel, monthsLabel, usersLabel,
} from '../utils/tariffs';
import { visuallyHidden } from '@mui/utils';
import { tokens } from '../theme';
import { isMember } from '../utils/access';

const QUOTE_DELAY = 300;

const SoonChip = ({ sx }) => (
  <Chip size="small" label="скоро" sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 700, bgcolor: tokens.warningSoft, color: tokens.warning, ...sx }} />
);

const CheckMark = ({ on }) => (
  <Box aria-hidden sx={{
    width: 22, height: 22, borderRadius: '7px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: `2px solid ${on ? tokens.primary : tokens.borderStrong}`, bgcolor: on ? tokens.primary : tokens.paper, color: '#fff',
    transition: 'background-color .15s, border-color .15s',
  }}>
    {on && <CheckRoundedIcon sx={{ fontSize: 16 }} />}
  </Box>
);

const Limit = ({ icon, children, tone = 'teal' }) => (
  <Box component="span" sx={{
    display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.375, borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600,
    bgcolor: tone === 'amber' ? tokens.warningSoft : tokens.primarySoft, color: tone === 'amber' ? tokens.warning : tokens.primaryDark,
  }}>
    {icon}{children}
  </Box>
);

// One plan of a marketplace module, selectable like a checkbox.
// compact: a smaller card for single-plan modules (Ozon, Яндекс Маркет) under the main WB plans.
const PlanCard = ({ plan, marketplace, selected, onToggle, catalog, grow = false, compact = false }) => (
  <ButtonBase role="checkbox" aria-checked={selected} onClick={onToggle} aria-label={`${marketplace.label} — тариф «${plan.name}»`}
    sx={{
      position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', textAlign: 'left', flex: grow ? 1 : undefined,
      p: compact ? 1.75 : 2.25, borderRadius: compact ? '14px' : '16px', border: `2px solid ${selected ? tokens.primary : tokens.border}`,
      bgcolor: selected ? tokens.primaryTint : tokens.paper, transition: 'border-color .15s, box-shadow .15s, background-color .15s',
      '&:hover': { boxShadow: tokens.shadowRaised, borderColor: selected ? tokens.primary : tokens.borderStrong },
    }}>
    {plan.popular && (
      <Box sx={{ position: 'absolute', top: -11, right: 14, px: 1.25, py: 0.25, borderRadius: '8px', bgcolor: tokens.primary, color: '#fff', fontSize: '0.6875rem', fontWeight: 700 }}>
        Популярный
      </Box>
    )}
    <Stack direction="row" spacing={1.25} alignItems="center">
      <CheckMark on={selected} />
      <Typography sx={{ fontWeight: 700, fontSize: compact ? '1rem' : '1.125rem' }}>{plan.name}</Typography>
      {plan.coming_soon && <SoonChip />}
    </Stack>
    <Box sx={{ mt: compact ? 1 : 1.5 }}>
      {plan.coming_soon ? (
        <>
          <Typography sx={{ fontSize: compact ? '1.25rem' : '1.625rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
            0 ₽ <Typography component="span" sx={{ fontSize: '0.8125rem', color: tokens.inkMuted, fontWeight: 500 }}>/мес</Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary">бесплатно до запуска загрузки данных, затем {formatMoney(plan.monthly_price)}/мес</Typography>
        </>
      ) : (
        <Typography sx={{ fontSize: compact ? '1.25rem' : '1.625rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
          {formatMoney(plan.monthly_price)} <Typography component="span" sx={{ fontSize: '0.8125rem', color: tokens.inkMuted, fontWeight: 500 }}>/мес</Typography>
        </Typography>
      )}
    </Box>
    {!compact && (
      <Typography variant="caption" sx={{ mt: 1.5, mb: 0.75, color: tokens.inkMuted, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Входит</Typography>
    )}
    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mt: compact ? 1 : 0 }}>
      <Limit tone="amber" icon={<PersonOutlineRoundedIcon sx={{ fontSize: 14 }} />}>{usersLabel(catalog.users.included)}</Limit>
      <Limit icon={<StorefrontOutlinedIcon sx={{ fontSize: 14 }} />}>{accountsLabel(catalog.accounts.included)}</Limit>
      <Limit>{marketplace.label}</Limit>
    </Stack>
    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: compact ? 1 : 1.5 }}>
      {plan.features.map((f) => (
        <Box component="li" key={f.text} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', my: compact ? 0.5 : 0.75, fontSize: compact ? '0.8125rem' : '0.875rem', lineHeight: 1.35, color: tokens.ink }}>
          <CheckRoundedIcon sx={{ fontSize: 16, color: tokens.success, mt: '1px', flexShrink: 0 }} />
          <span>{f.text}{f.soon && <SoonChip sx={{ ml: 0.75, verticalAlign: 'middle' }} />}</span>
        </Box>
      ))}
    </Box>
  </ButtonBase>
);

// Accounts of one marketplace: one is included, each further one adds a share of the module price.
// Without a chosen plan the marketplace has no module, so nothing can be added.
const AccountsStepper = ({ marketplace, plan, value, onChange, catalog }) => {
  const { included, max, extra_percent: pct } = catalog.accounts;
  const unit = plan && !plan.coming_soon ? Math.round((plan.monthly_price * pct) / 100) : 0;
  const extra = Math.max(0, value - included);
  return (
    <Stack direction="row" spacing={2} alignItems="center"
      sx={{ px: 2, py: 1.5, borderRadius: '12px', bgcolor: plan ? tokens.paper : tokens.bg, border: `1px solid ${tokens.border}` }}>
      <MarketplaceIcon marketplace={marketplace.key} size={32} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 600 }}>{marketplace.label}</Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {!plan ? `Сначала выберите тариф ${marketplace.label}`
            : `${included} входит в тариф · каждый следующий ${unit > 0 ? `+${formatMoney(unit)}/мес` : 'без доплаты'} · до ${max}`}
        </Typography>
        {plan && extra > 0 && unit > 0 && (
          <Typography variant="caption" sx={{ color: tokens.primaryDark, fontWeight: 600 }} component="div">
            Доплата: {extra} × {formatMoney(unit)} = {formatMoney(extra * unit)}/мес
          </Typography>
        )}
      </Box>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
        <IconButton size="small" aria-label={`Меньше кабинетов ${marketplace.label}`} disabled={!plan || value <= 1} onClick={() => onChange(value - 1)}
          sx={{ border: `1px solid ${tokens.borderStrong}`, borderRadius: '10px', bgcolor: tokens.paper }}>
          <RemoveRoundedIcon fontSize="small" />
        </IconButton>
        <Typography aria-live="polite" sx={{ minWidth: 32, textAlign: 'center', fontWeight: 700, fontSize: '1.125rem', color: plan ? tokens.ink : tokens.inkMuted }}>{plan ? value : '—'}</Typography>
        <IconButton size="small" aria-label={`Больше кабинетов ${marketplace.label}`} disabled={!plan || value >= max} onClick={() => onChange(value + 1)}
          sx={{ border: `1px solid ${tokens.borderStrong}`, borderRadius: '10px', bgcolor: tokens.paper }}>
          <AddRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  );
};

// "Additional accounts": one dialog for the accounts of every marketplace.
const AccountsDialog = ({ open, onClose, catalog, plans, accounts, onChange }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ pr: 7 }}>
      Дополнительные кабинеты
      <IconButton aria-label="Закрыть" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}><CloseRoundedIcon /></IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        В тариф каждой площадки входит {accountsLabel(catalog.accounts.included)}. Если у вас несколько кабинетов на одной площадке, добавьте их здесь —
        каждый следующий стоит {catalog.accounts.extra_percent}% цены тарифа площадки.
      </Typography>
      <Stack spacing={1.25}>
        {catalog.marketplaces.map((m) => (
          <AccountsStepper key={m.key} marketplace={m} catalog={catalog} plan={m.plans.find((p) => p.id === plans[m.key])}
            value={accounts[m.key] || 1} onChange={(v) => onChange(m.key, v)} />
        ))}
      </Stack>
    </DialogContent>
    <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end' }}>
      <Button variant="contained" onClick={onClose}>Готово</Button>
    </Box>
  </Dialog>
);

const StepCard = ({ n, title, hint, children, action }) => (
  <Card>
    <CardContent>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="flex-start" spacing={{ xs: 0, sm: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3" component="h2">{n}. {title}</Typography>
          {hint && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.25 }}>{hint}</Typography>}
        </Box>
        {action}
      </Stack>
      {children}
    </CardContent>
  </Card>
);

// "Compare plans": which Wildberries level includes what, features in development marked.
const CompareDialog = ({ open, onClose, catalog }) => {
  const wb = catalog.marketplaces.find((m) => m.key === 'wildberries');
  const cols = catalog.comparison.columns.map((id) => wb?.plans.find((p) => p.id === id)).filter(Boolean);
  const otherPrice = catalog.marketplaces.find((m) => m.key !== 'wildberries')?.plans[0]?.monthly_price;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 7 }}>
        Сравнение тарифов Wildberries
        <IconButton aria-label="Закрыть" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}><CloseRoundedIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 260, bgcolor: tokens.paper }}>Возможность</TableCell>
                {cols.map((p) => (
                  <TableCell key={p.id} align="center" sx={{ minWidth: 110, bgcolor: tokens.paper }}>
                    <Typography sx={{ fontWeight: 700 }}>{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatMoney(p.monthly_price)}/мес</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {catalog.comparison.groups.map((g) => (
                <React.Fragment key={g.title}>
                  <TableRow>
                    <TableCell colSpan={cols.length + 1} sx={{ bgcolor: tokens.primaryTint, fontWeight: 700, color: tokens.primaryDark, py: 1 }}>{g.title}</TableCell>
                  </TableRow>
                  {g.rows.map((r) => (
                    <TableRow key={r.name} hover>
                      <TableCell>
                        {r.name}{r.soon && <SoonChip sx={{ ml: 1, verticalAlign: 'middle' }} />}
                      </TableCell>
                      {cols.map((p) => (
                        <TableCell key={p.id} align="center">
                          {r.plans.includes(p.id)
                            ? <CheckRoundedIcon aria-label="входит" sx={{ color: r.soon ? tokens.inkMuted : tokens.success }} />
                            : <Typography component="span" aria-label="не входит" sx={{ color: tokens.inkMuted }}>—</Typography>}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Box sx={{ p: 2.5, bgcolor: tokens.bg }}>
          <Typography variant="body2" color="text.secondary">
            В каждый тариф входят {usersLabel(catalog.users.included)} и {accountsLabel(catalog.accounts.included)} площадки.
            Ozon и Яндекс Маркет — модуль «Базовый»{otherPrice ? ` за ${formatMoney(otherPrice)}/мес` : ''}.
            Отметка «скоро» — возможность в разработке.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const TRUST = [
  'Без автосписаний — оплата только по счёту',
  'Цена из заявки фиксируется в счёте',
  'Счёт для ИП и ООО на реквизиты компании',
];
const TRUST_ONLINE = [
  'Без автосписаний — оплачиваете только выбранный срок',
  'Цена из заявки фиксируется на весь срок',
  'Оплата картой, СБП и другими способами через ЮKassa',
  'Чек придёт на вашу почту',
];

const faq = (email, online) => [
  ['Как проходит оплата?', online
    ? 'Оформите заявку и нажмите «Оплатить»: откроется страница ЮKassa, где можно заплатить картой, через СБП или другим способом. Доступ продлится сразу после оплаты. Если нужен счёт для организации, напишите нам.'
    : `После заявки мы свяжемся с вами${email ? ` по почте ${email}` : ''}, уточним реквизиты и выставим счёт. Доступ продлим после оплаты. Онлайн-оплаты на сайте пока нет.`],
  ['Что входит в модуль площадки?', 'Аналитика выбранной площадки для одного кабинета и один пользователь. Дополнительные кабинеты и пользователи добавляются к модулю за доплату.'],
  ['Как добавить ещё кабинет?', 'Кнопка «Дополнительные кабинеты» в первом шаге. Один кабинет каждой площадки входит в тариф, каждый следующий стоит половину цены тарифа этой площадки.'],
  ['Можно ли изменить заявку?', 'Да. Отправьте новую — пока мы не обработали предыдущую, новая её заменит.'],
];

const SummaryRow = ({ line, months }) => {
  const discount = isDiscountLine(line);
  const detail = lineDetail(line, months);
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ py: 0.75 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: discount ? tokens.success : tokens.ink, fontWeight: 500 }}>{lineLabel(line)}</Typography>
        {detail && <Typography variant="caption" color="text.secondary" component="div">{detail}</Typography>}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: discount ? tokens.success : tokens.ink }}>
        {formatSignedMoney(line.amount)}
      </Typography>
    </Stack>
  );
};


// Users: a slider for the usual 1–10 and −/+ for any larger team; the price of extra seats is
// shown right away.
const SLIDER_MAX = 10;
const UsersPicker = ({ value, onChange, rule }) => {
  const set = (v) => onChange(Math.max(1, Math.min(rule.max, Math.round(Number(v) || 1))));
  const extra = Math.max(0, value - rule.included);
  const stepBtn = (label, icon, onClick, disabled) => (
    <IconButton aria-label={label} onClick={onClick} disabled={disabled}
      sx={{ width: 44, height: 44, borderRadius: '12px', border: `1px solid ${tokens.border}`, bgcolor: tokens.paper,
        '&:hover': { bgcolor: tokens.primaryTint, borderColor: tokens.primary } }}>
      {icon}
    </IconButton>
  );
  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto minmax(0, 1fr)' }, gap: { xs: 2.5, md: 4 }, alignItems: 'center' }}>
        <Stack direction="row" spacing={1.25} alignItems="center" justifyContent={{ xs: 'center', md: 'flex-start' }}>
          {stepBtn('Убрать пользователя', <RemoveRoundedIcon />, () => set(value - 1), value <= 1)}
          <Box sx={{ minWidth: 104, textAlign: 'center', px: 1.5, py: 0.75, borderRadius: '14px', bgcolor: tokens.primaryTint, border: `1px solid ${tokens.border}` }}>
            <TextField variant="standard" value={value} onChange={(e) => set(e.target.value.replace(/\D/g, '') || 1)}
              inputProps={{ inputMode: 'numeric', 'aria-label': 'Количество пользователей', style: { textAlign: 'center', fontSize: '2rem', fontWeight: 700, padding: 0, width: 80 } }}
              InputProps={{ disableUnderline: true }} />
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: -0.25 }}>{pluralize(value, 'пользователь', 'пользователя', 'пользователей')}</Typography>
          </Box>
          {stepBtn('Добавить пользователя', <AddRoundedIcon />, () => set(value + 1), value >= rule.max)}
        </Stack>
        <Box sx={{ px: { xs: 1, md: 0 } }}>
          {/* Every value 1–10 has its own visible step and label, so the thumb snaps to clear points. */}
          <Slider value={Math.min(value, SLIDER_MAX)} min={1} max={SLIDER_MAX} step={1} onChange={(_, v) => set(v)}
            marks={Array.from({ length: SLIDER_MAX }, (_, i) => i + 1).map((v) => ({ value: v, label: v === SLIDER_MAX ? '10+' : String(v) }))}
            valueLabelDisplay="off" aria-label="Количество пользователей, ползунок"
            aria-valuetext={`${value} ${pluralize(value, 'пользователь', 'пользователя', 'пользователей')}`}
            sx={{ height: 6, py: '18px', mt: 0.5,
              '& .MuiSlider-rail': { opacity: 1, bgcolor: tokens.primarySoft },
              '& .MuiSlider-track': { border: 'none', background: `linear-gradient(90deg, ${tokens.primarySoft}, ${tokens.primary})`, transition: 'none' },
              '& .MuiSlider-thumb': { width: 24, height: 24, bgcolor: '#fff', border: `3px solid ${tokens.primary}`, boxShadow: '0 4px 12px rgba(8,187,181,.25)', transition: 'box-shadow .15s', '&:hover, &.Mui-focusVisible, &.Mui-active': { boxShadow: '0 0 0 8px rgba(8,187,181,.16)' } },
              '& .MuiSlider-mark': { width: 4, height: 4, borderRadius: '50%', bgcolor: tokens.primary, opacity: 0.45 },
              '& .MuiSlider-markActive': { bgcolor: '#fff', opacity: 0.9 },
              '& .MuiSlider-markLabel': { fontSize: '0.8125rem', color: tokens.inkMuted, top: 38 },
              '& .MuiSlider-markLabelActive': { color: tokens.ink },
              [`& .MuiSlider-markLabel[data-index="${Math.min(value, SLIDER_MAX) - 1}"]`]: { color: tokens.primaryDark, fontWeight: 700 } }} />
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
            Больше {SLIDER_MAX}? Нажмите «+» или введите число — ограничения нет.
          </Typography>
        </Box>
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2.5 }}>
        <Box sx={{ flex: 1, px: 2, py: 1.5, borderRadius: '12px', bgcolor: tokens.bg, border: `1px solid ${tokens.border}` }}>
          <Typography variant="caption" color="text.secondary">Входит в тариф</Typography>
          <Typography sx={{ fontWeight: 700 }}>{usersLabel(rule.included)}</Typography>
        </Box>
        <Box sx={{ flex: 1, px: 2, py: 1.5, borderRadius: '12px', bgcolor: tokens.bg, border: `1px solid ${tokens.border}` }}>
          <Typography variant="caption" color="text.secondary">Каждый следующий</Typography>
          <Typography sx={{ fontWeight: 700 }}>+{formatMoney(rule.price)} в месяц</Typography>
        </Box>
        <Box sx={{ flex: 1, px: 2, py: 1.5, borderRadius: '12px', bgcolor: extra ? tokens.primaryTint : tokens.bg, border: `1px solid ${extra ? tokens.primary : tokens.border}` }}>
          <Typography variant="caption" color="text.secondary">Доплата за {extra} {pluralize(extra, 'место', 'места', 'мест')}</Typography>
          <Typography sx={{ fontWeight: 700, color: extra ? tokens.primaryDark : tokens.ink }}>{extra ? `${formatMoney(extra * rule.price)} в месяц` : 'не нужна'}</Typography>
        </Box>
      </Stack>
    </Box>
  );
};

const TariffsPage = () => {
  const { profile } = useShell();
  const [catalog, setCatalog] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [plans, setPlans] = useState({ wildberries: 'wb_analyst' }); // marketplace -> plan id
  const [accounts, setAccounts] = useState({});
  const [users, setUsers] = useState(1);
  const [months, setMonths] = useState(3);
  const [promo, setPromo] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [promoMsg, setPromoMsg] = useState(null); // { ok, text }
  const [promoBusy, setPromoBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [compareOpen, setCompareOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [sent, setSent] = useState(null); // { request, replaced }
  const [openRequest, setOpenRequest] = useState(null);
  const [limits, setLimits] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');
  const [returned, setReturned] = useState(null); // payment the user came back to from the payment page
  const [testBusy, setTestBusy] = useState(false);
  const [testError, setTestError] = useState('');
  const quoteSeq = useRef(0);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const [cat, mine, lim] = await Promise.all([tariffsApi.catalog(), tariffsApi.myRequests().catch(() => ({ data: [] })),
        tariffsApi.limits().catch(() => ({ data: null }))]);
      setCatalog(cat.data);
      setLimits(lim.data);
      setOpenRequest((mine.data || []).find((r) => r.status === 'new') || null);
    } catch (e) {
      setLoadError(apiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Back from the payment page: ?payment=<id>. The server asks YooKassa, so the answer is right
  // even if the notification has not arrived; while it is still pending, ask again a few times.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('payment');
    if (!id) return undefined;
    let stop = false;
    let tries = 0;
    const check = async () => {
      try {
        const r = await tariffsApi.payment(id);
        if (stop) return;
        setReturned(r.data);
        if (r.data.status === 'pending' && tries++ < 5) setTimeout(check, 3000);
        else if (r.data.status === 'succeeded') load();
      } catch (e) {
        if (!stop) setReturned({ status: 'error', error: apiError(e) });
      }
    };
    check();
    return () => { stop = true; };
  }, [load]);

  const modules = useMemo(() => Object.entries(plans)
    .filter(([, plan]) => plan)
    .map(([marketplace, plan]) => ({ marketplace, plan, accounts: accounts[marketplace] || 1 })), [plans, accounts]);

  const selection = useMemo(() => ({ modules, users, months, promo_code: promo || undefined }), [modules, users, months, promo]);

  // Every change is priced by the server, debounced; late answers to old selections are dropped.
  useEffect(() => {
    if (!catalog) return undefined;
    if (modules.length === 0) {
      quoteSeq.current += 1;
      setQuote(null);
      setQuoteError('');
      setQuoting(false);
      return undefined;
    }
    const seq = ++quoteSeq.current;
    setQuoting(true);
    const t = setTimeout(async () => {
      try {
        const r = await tariffsApi.quote(selection);
        if (seq !== quoteSeq.current) return;
        setQuote(r.data);
        setQuoteError('');
      } catch (e) {
        if (seq !== quoteSeq.current) return;
        if (e?.response?.data?.field === 'promo_code') {
          // the code stopped working (expired, used up) since it was applied
          setPromo('');
          setPromoMsg({ ok: false, text: e.response.data.error });
          return;
        }
        setQuoteError(apiError(e));
      } finally {
        if (seq === quoteSeq.current) setQuoting(false);
      }
    }, QUOTE_DELAY);
    return () => clearTimeout(t);
  }, [catalog, modules, selection]);

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) {
      setPromoMsg({ ok: false, text: 'Введите промокод' });
      return;
    }
    if (modules.length === 0) {
      setPromoMsg({ ok: false, text: 'Сначала выберите площадку' });
      return;
    }
    setPromoBusy(true);
    try {
      const r = await tariffsApi.quote({ ...selection, promo_code: code });
      setPromo(r.data.promo.code);
      setPromoInput(r.data.promo.code);
      setPromoMsg({ ok: true, text: `Промокод применён: −${r.data.promo.percent}%` });
    } catch (e) {
      setPromo('');
      setPromoMsg({ ok: false, text: e?.response?.data?.field === 'promo_code' ? e.response.data.error : apiError(e) });
    } finally {
      setPromoBusy(false);
    }
  };

  const removePromo = () => {
    setPromo('');
    setPromoInput('');
    setPromoMsg(null);
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const r = await tariffsApi.createRequest(selection);
      setSent(r.data);
      setOpenRequest(r.data.request);
    } catch (e) {
      if (e?.response?.data?.field === 'promo_code') {
        setPromo('');
        setPromoMsg({ ok: false, text: e.response.data.error });
      }
      setSubmitError(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const pay = async (requestId) => {
    setPaying(true);
    setPayError('');
    try {
      const r = await tariffsApi.pay(requestId);
      if (r.data.confirmation_url) {
        window.location.assign(r.data.confirmation_url);
        return;
      }
      setPayError('Платёжная система не вернула ссылку на оплату. Попробуйте ещё раз.');
    } catch (e) {
      setPayError(apiError(e));
    }
    setPaying(false);
  };

  // the one-ruble test tariff: its own request, paid at once when online payment is on
  const buyTest = async () => {
    setTestBusy(true);
    setTestError('');
    try {
      const r = await tariffsApi.createRequest({ modules: [{ marketplace: 'test', plan: catalog.test.id, accounts: 1 }], users: 1, months: catalog.test.months });
      setOpenRequest(r.data.request);
      if (catalog.online_payment) {
        await pay(r.data.request.id);
      } else {
        setSent(r.data);
      }
    } catch (e) {
      setTestError(apiError(e));
    } finally {
      setTestBusy(false);
    }
  };

  const togglePlan = (mp, planId) => setPlans((p) => ({ ...p, [mp]: p[mp] === planId ? null : planId }));
  const contactEmail = profile?.email?.includes('@') ? profile.email : '';

  // an employee works under the owner's tariff; tariffs are the owner's
  if (isMember(profile)) return <Navigate to="/dashboard" replace />;

  if (loadError) {
    return (
      <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
        <Box component="h1" sx={visuallyHidden}>Тарифы MARQIT</Box>
        <ErrorState message={loadError} onRetry={load} />
      </Box>
    );
  }

  if (!catalog) {
    return (
      <Box sx={{ maxWidth: 1480, mx: 'auto' }} aria-busy="true">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) 380px' }, gap: 3 }}>
          <Skeleton variant="rounded" height={520} sx={{ borderRadius: '16px' }} />
          <Skeleton variant="rounded" height={420} sx={{ borderRadius: '16px' }} />
        </Box>
      </Box>
    );
  }

  const wb = catalog.marketplaces.find((m) => m.key === 'wildberries');
  const others = catalog.marketplaces.filter((m) => m.key !== 'wildberries');
  const selectedPlan = (m) => m.plans.find((p) => p.id === plans[m.key]);
  const accountsSummary = catalog.marketplaces.filter((m) => selectedPlan(m))
    .map((m) => `${m.label} — ${accounts[m.key] || 1}`).join(' · ');
  const period = catalog.periods.find((p) => p.months === months);


  const periodPicker = (
    <Box sx={{ mb: 2 }}>
      <Typography variant="overline" sx={{ color: tokens.primaryDark, display: 'block', mb: 0.75 }}>Срок подписки</Typography>
      <Box role="radiogroup" aria-label="Срок подписки"
        sx={{ display: 'grid', gridTemplateColumns: `repeat(${catalog.periods.length}, 1fr)`, gap: 0.5, p: 0.5, borderRadius: '12px', bgcolor: tokens.paper, border: `1px solid ${tokens.border}` }}>
        {catalog.periods.map((p) => {
          const on = p.months === months;
          return (
            <ButtonBase key={p.months} role="radio" aria-checked={on} onClick={() => setMonths(p.months)} aria-label={`${monthsLabel(p.months)}${p.discount_percent ? `, скидка ${p.discount_percent}%` : ''}`}
              sx={{ flexDirection: 'column', py: 0.75, borderRadius: '9px', bgcolor: on ? tokens.primary : 'transparent', color: on ? '#fff' : tokens.ink,
                transition: 'background-color .15s', '&:hover': { bgcolor: on ? tokens.primary : tokens.primaryTint } }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', lineHeight: 1.2 }}>{p.months} мес</Typography>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, lineHeight: 1.3, color: on ? 'rgba(255,255,255,.85)' : (p.discount_percent ? tokens.success : tokens.inkMuted) }}>
                {p.discount_percent ? `−${p.discount_percent}%` : '—'}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
      {period && period.discount_percent > 0 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
          Скидка {period.discount_percent}% за оплату сразу на {monthsLabel(period.months)}{promo ? '; промокод применяется после неё' : ''}. Цена фиксируется на весь срок.
        </Typography>
      )}
    </Box>
  );

  const summary = sent ? (
    <Card sx={{ position: { lg: 'sticky' }, top: { lg: 24 } }}>
      <CardContent sx={{ textAlign: 'center', py: 5 }}>
        <Box sx={{ width: 64, height: 64, mx: 'auto', mb: 2, borderRadius: '18px', bgcolor: tokens.successSoft, color: tokens.success, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <TaskAltRoundedIcon sx={{ fontSize: 36 }} />
        </Box>
        <Typography variant="h2" component="h2">Заявка отправлена</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          {catalog.online_payment
            ? <>К оплате <b style={{ color: tokens.ink }}>{formatMoney(sent.request.total)}</b> за {monthsLabel(sent.request.months)}. Нажмите «Оплатить», чтобы перейти на страницу оплаты.</>
            : <>Выставим счёт на <b style={{ color: tokens.ink }}>{formatMoney(sent.request.total)}</b> за {monthsLabel(sent.request.months)} и свяжемся с вами
              {contactEmail ? ` по почте ${contactEmail}` : ''}.</>}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
          Заявка № {sent.request.id.slice(0, 8).toUpperCase()} от {formatDate(sent.request.created_at)}
          {sent.replaced > 0 ? ' · предыдущая заявка заменена' : ''}
        </Typography>
        {payError && <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>{payError}</Alert>}
        <Stack spacing={1} sx={{ mt: 3 }}>
          {catalog.online_payment && (
            <Button variant="contained" size="large" onClick={() => pay(sent.request.id)} disabled={paying}
              startIcon={paying ? <CircularProgress size={18} color="inherit" /> : null} sx={{ height: 52, fontSize: '1rem' }}>
              Оплатить {formatMoney(sent.request.total)}
            </Button>
          )}
          <Button variant="outlined" onClick={() => setSent(null)}>Изменить заявку</Button>
          <Button component={RouterLink} to="/dashboard">На главную</Button>
        </Stack>
      </CardContent>
    </Card>
  ) : (
    <Card sx={{ position: { lg: 'sticky' }, top: { lg: 24 }, overflow: 'hidden' }}>
      <Box sx={{ background: tokens.heroGradient, px: 3, pt: 3, pb: 2.5 }}>
        {periodPicker}
        <Typography variant="overline" sx={{ color: tokens.primaryDark }}>Итого к оплате</Typography>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography aria-live="polite" sx={{ fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15, opacity: quoting ? 0.55 : 1, transition: 'opacity .15s' }}>
            {quote ? formatMoney(quote.total) : '—'}
          </Typography>
          {quoting && <CircularProgress size={20} aria-label="Пересчёт" />}
        </Stack>
        <Typography color="text.secondary" sx={{ mt: 0.25 }}>
          {quote ? `за ${monthsLabel(quote.months)} · ${formatMoney(quote.monthly_equivalent)} в месяц` : 'Выберите хотя бы одну площадку'}
        </Typography>
        {quote && quote.discount_total > 0 && (
          <Typography variant="body2" sx={{ mt: 0.5, color: tokens.success, fontWeight: 600 }}>
            Экономия {formatMoney(quote.discount_total)} <Box component="s" sx={{ color: tokens.inkMuted, fontWeight: 500, ml: 0.5 }}>{formatMoney(quote.list_total)}</Box>
          </Typography>
        )}
      </Box>
      <CardContent sx={{ pt: 2 }}>
        {quoteError && <Alert severity="error" sx={{ mb: 1.5 }}>{quoteError}</Alert>}
        {quote && quote.lines.map((l, i) => <SummaryRow key={`${l.kind}-${l.marketplace || ''}-${i}`} line={l} months={quote.months} />)}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" component="label" htmlFor="promo-code" sx={{ display: 'block', mb: 1 }}>Промокод</Typography>
        {promo ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, borderRadius: '12px', bgcolor: tokens.successSoft }}>
            <CheckRoundedIcon sx={{ color: tokens.success, fontSize: 20 }} />
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>{promo}</Typography>
            <Button size="small" onClick={removePromo}>Убрать</Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1}>
            <TextField id="promo-code" size="small" fullWidth placeholder="Например, START10" value={promoInput}
              onChange={(e) => { setPromoInput(e.target.value); setPromoMsg(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') applyPromo(); }}
              inputProps={{ style: { textTransform: 'uppercase' }, maxLength: 40, 'aria-describedby': 'promo-msg' }} />
            <Button variant="outlined" onClick={applyPromo} disabled={promoBusy} sx={{ flexShrink: 0 }}>
              {promoBusy ? <CircularProgress size={18} /> : 'Применить'}
            </Button>
          </Stack>
        )}
        <Typography id="promo-msg" role="status" variant="body2" sx={{ mt: 0.75, minHeight: 20, color: promoMsg?.ok ? tokens.success : tokens.error }}>
          {promoMsg?.text || ''}
        </Typography>

        {submitError && <Alert severity="error" sx={{ mt: 1.5 }}>{submitError}</Alert>}
        <Button fullWidth variant="contained" size="large" onClick={submit} disabled={!quote || quoting || submitting || quote.total <= 0}
          sx={{ mt: 1.5, height: 52, fontSize: '1rem' }} startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}>
          Оформить заявку
        </Button>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ textAlign: 'center', mt: 1 }}>
          {quote && quote.total <= 0 ? 'Выбранные модули пока бесплатны — заявка не нужна'
            : (catalog.online_payment ? 'Сначала оформите заявку, затем оплатите её на странице ЮKassa' : 'Онлайн-оплаты пока нет: по заявке выставим счёт')}
        </Typography>

        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 2 }}>
          {(catalog.online_payment ? TRUST_ONLINE : TRUST).map((t) => (
            <Box component="li" key={t} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', my: 0.75, fontSize: '0.8125rem', color: tokens.inkSoft }}>
              <CheckRoundedIcon sx={{ fontSize: 16, color: tokens.success, mt: '1px' }} />{t}
            </Box>
          ))}
        </Box>
        <Box sx={{ mt: 1.5 }}>
          {faq(contactEmail, catalog.online_payment).map(([q, a]) => (
            <Accordion key={q} disableGutters elevation={0} square
              sx={{ bgcolor: 'transparent', borderTop: `1px solid ${tokens.border}`, '&::before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 0, minHeight: 44, '& .MuiAccordionSummary-content': { my: 1 } }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 0 }}>
                <Typography variant="body2" color="text.secondary">{a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
      <Box component="h1" sx={visuallyHidden}>Тарифы MARQIT</Box>

      <TrialCard />

      {catalog.test && <TestTariffCard plan={catalog.test} onlinePayment={catalog.online_payment} busy={testBusy || paying} error={testError} onBuy={buyTest} />}

      {limits && limits.source !== 'unlimited' && (
        <Alert severity="info" icon={<TaskAltRoundedIcon />} sx={{ mb: 2 }}>
          {limits.source === 'plan'
            ? `Ваш тариф действует до ${formatDate(limits.until)}. `
            : 'Сейчас у вас пробный доступ. '}
          Можно подключить: {catalog.marketplaces.filter((m) => limits.accounts?.[m.key])
            .map((m) => `${m.label} — ${limits.accounts[m.key]}`).join(', ') || 'нет площадок'}
          {'; сотрудников — '}{limits.employees}.
        </Alert>
      )}

      {returned && (
        <Alert severity={returned.status === 'succeeded' ? 'success' : (returned.status === 'pending' ? 'info' : 'warning')} sx={{ mb: 2 }} role="status">
          {returned.status === 'succeeded' && `Оплата ${formatMoney(returned.amount)} прошла, доступ продлён. Спасибо!`}
          {returned.status === 'pending' && 'Ждём подтверждение оплаты от банка. Обычно это занимает меньше минуты, страница обновится сама.'}
          {returned.status === 'canceled' && 'Оплата не прошла, деньги не списаны. Можно попробовать ещё раз ниже.'}
          {returned.status === 'error' && `Не удалось проверить оплату: ${returned.error}`}
        </Alert>
      )}

      {openRequest && !sent && (
        <Alert severity="info" sx={{ mb: 3 }}
          action={catalog.online_payment ? (
            <Button color="inherit" size="small" onClick={() => pay(openRequest.id)} disabled={paying}>Оплатить</Button>
          ) : undefined}>
          {catalog.online_payment
            ? `Ваша заявка от ${formatDate(openRequest.created_at)} на ${formatMoney(openRequest.total)} ещё не оплачена. Новая заявка заменит её.`
            : `Ваша заявка от ${formatDate(openRequest.created_at)} на ${formatMoney(openRequest.total)} ждёт обработки — мы выставим счёт. Новая заявка заменит её.`}
          {payError && <Box component="span" sx={{ display: 'block', color: tokens.error }}>{payError}</Box>}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) 380px' }, gap: 3, alignItems: 'start' }}>
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          <StepCard n={1} title="Площадки и тариф"
            hint={`Отметьте нужные площадки. В каждый модуль уже входят ${usersLabel(catalog.users.included)} и ${accountsLabel(catalog.accounts.included)} площадки.`}
            action={(
              <Button size="small" startIcon={<TableChartOutlinedIcon />} onClick={() => setCompareOpen(true)} sx={{ flexShrink: 0, color: tokens.primaryDark, ml: { xs: -1.5, sm: 0 }, mb: { xs: 1.5, sm: 0 } }}>
                Сравнить тарифы
              </Button>
            )}>
            {wb && (
              <Box>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                  <MarketplaceIcon marketplace="wildberries" size={28} />
                  <Typography variant="h5" component="h3">{wb.label}</Typography>
                  <Typography variant="body2" color="text.secondary">— выберите уровень</Typography>
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.75, pt: 1 }}>
                  {wb.plans.map((p) => (
                    <PlanCard key={p.id} plan={p} marketplace={wb} catalog={catalog} selected={plans[wb.key] === p.id} onToggle={() => togglePlan(wb.key, p.id)} />
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.75, mt: 3 }}>
              {others.map((m) => (
                <Box key={m.key} sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <MarketplaceIcon marketplace={m.key} size={24} />
                    <Typography variant="h6" component="h3">{m.label}</Typography>
                  </Stack>
                  {m.plans.map((p) => (
                    <PlanCard key={p.id} grow compact plan={p} marketplace={m} catalog={catalog} selected={plans[m.key] === p.id} onToggle={() => togglePlan(m.key, p.id)} />
                  ))}
                </Box>
              ))}
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ sm: 'center' }}
              sx={{ mt: 2.5, px: 2, py: 1.5, borderRadius: '12px', border: `1px solid ${tokens.border}` }}>
              <StorefrontOutlinedIcon sx={{ color: tokens.primaryDark, display: { xs: 'none', sm: 'block' } }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Кабинеты площадок</Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  {accountsSummary ? `${accountsSummary}. ` : ''}Несколько кабинетов на одной площадке — добавьте их здесь
                </Typography>
              </Box>
              <Button variant="outlined" size="small" startIcon={<AddRoundedIcon />} onClick={() => setAccountsOpen(true)} sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                Дополнительные кабинеты
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1.5, px: 2, py: 1.5, borderRadius: '12px', bgcolor: tokens.primaryTint, border: `1px solid ${tokens.border}` }}>
              <InfoOutlinedIcon sx={{ fontSize: 20, color: tokens.primaryDark, mt: '1px' }} />
              <Typography variant="body2" color="text.secondary">
                Пакет из {catalog.bundle.min_paid_marketplaces} и более платных площадок — <b style={{ color: tokens.ink }}>скидка {catalog.bundle.percent}%</b> на модули и их кабинеты.
                {quote?.bundle_applied ? ' Скидка применена.' : ''}
              </Typography>
            </Stack>
          </StepCard>

          <StepCard n={2} title="Пользователи" hint="Сколько человек будет работать в сервисе: вы, бухгалтер, менеджеры, финансист. Роли и доступы сотрудникам вы настроите в разделе «Команда».">
            <UsersPicker value={users} onChange={setUsers} rule={catalog.users} />
          </StepCard>

        </Stack>

        {summary}
      </Box>

      <CompareDialog open={compareOpen} onClose={() => setCompareOpen(false)} catalog={catalog} />
      <AccountsDialog open={accountsOpen} onClose={() => setAccountsOpen(false)} catalog={catalog} plans={plans} accounts={accounts}
        onChange={(mp, v) => setAccounts((a) => ({ ...a, [mp]: v }))} />
    </Box>
  );
};

export default TariffsPage;

